import React from 'react';
import {
  BarChart2,
  TrendingUp,
  TrendingDown,
  Minus,
  ExternalLink,
  Edit2,
  Info,
  AlertTriangle,
  Activity,
  Users,
  Clock,
  DollarSign,
  Smile,
  Target as TargetIcon,
} from 'lucide-react';
import { AgentData_V10, AgentHubPageViewMode, PerformanceStatV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { motion } from 'framer-motion';
import { Tooltip } from '@dopomogai/ui';
import { useTranslation } from 'react-i18next';
import { renderIcon } from '@dopomogai/utils';
import clsx from 'clsx';
import {
  INNER_CARD_ANIMATE_RESTING,
  INNER_CARD_HOVER,
  INNER_CARD_TRANSITION_HOVER
} from '../../shared/animationConstants';


interface PerformanceSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

// Helper to determine trend icon
const getTrendIcon = (trend?: 'positive' | 'negative' | 'neutral' | 'up' | 'down') => {
  switch (trend) {
    case 'positive': case 'up': return <TrendingUp size={24} className="text-emerald-500" />;
    case 'negative': case 'down': return <TrendingDown size={24} className="text-rose-500" />;
    default: return <Minus size={24} className="text-slate-400" />;
  }
};

// --- NEW Hero Stat Card for Public View ---
const HeroStatCard: React.FC<{ stat: PerformanceStatV10 }> = ({ stat }) => {
    // A slightly more pronounced hover effect for the main card with a themed shadow
    const HERO_STAT_HOVER = {
        x: -4,
        y: -4,
        // ENHANCEMENT: Shadow color is now toned to match the orange theme
        boxShadow: "4px 4px 0px 0px #F97316", // This is orange-500
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ ...INNER_CARD_ANIMATE_RESTING, opacity: 1, y: 0 }}
            whileHover={HERO_STAT_HOVER}
            transition={INNER_CARD_TRANSITION_HOVER}
            className="bg-orange-50/70 border-2 border-black rounded-xl p-6 flex flex-col justify-between h-full"
        >
            <div className="flex justify-between items-start">
            <div className="text-orange-600 p-2.5 bg-white rounded-lg border-2 border-black shadow-sm">
                {renderIcon(stat.icon, { size: 32 })}
            </div>
            {stat.trend && getTrendIcon(stat.trend)}
            </div>
            <div className="mt-4 text-left">
            <p className="text-5xl lg:text-6xl font-extrabold text-gray-900 mb-1">
                {stat.value}
            </p>
            <p className="text-base font-semibold text-orange-800 capitalize">
                {stat.label}
            </p>
            </div>
        </motion.div>
    );
};

// --- Refined Stat Card for secondary stats and owner view ---
const StatDisplayCard: React.FC<{ stat: PerformanceStatV10, index: number }> = ({ stat, index }) => {
  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ ...INNER_CARD_ANIMATE_RESTING, opacity: 1, y: 0, transition: { delay: index * 0.05 } }}
      whileHover={INNER_CARD_HOVER} // Using the standard inner card hover from constants
      transition={INNER_CARD_TRANSITION_HOVER}
      className="bg-white border-2 border-black rounded-xl p-5 flex flex-col justify-between h-full"
    >
      <div className="flex justify-between items-start mb-2">
        <div className="text-slate-500 p-2 bg-slate-100 rounded-md border-2 border-black shadow-sm">
          {renderIcon(stat.icon, { size: 20 })}
        </div>
        {stat.trend && getTrendIcon(stat.trend)}
      </div>
      <div className="mt-2">
        <p className="text-3xl font-bold text-gray-900 mb-0.5">
          {stat.value}
        </p>
        <p className="text-sm text-slate-600 capitalize leading-tight">
          {stat.label}
        </p>
      </div>
    </motion.div>
  );

  return (stat.tooltip || stat.dataSourceInfo) ? (
    <Tooltip text={stat.tooltip || stat.dataSourceInfo!} position="top">
      {cardContent}
    </Tooltip>
  ) : cardContent;
};


export const PerformanceSection = React.forwardRef<HTMLDivElement, PerformanceSectionProps>(
  ({ agentData, viewMode, onAction, headerBgColor, id }, ref) => {
  const { t } = useTranslation('agent-common');
  const isOwnerView = viewMode === 'owner_dashboard';

  const PublicPerformanceView = () => {
    const publicStats = agentData.performanceHighlightsPublic?.stats;
    if (!publicStats || publicStats.length === 0) {
      return <p className="text-center text-slate-500 py-8">{t('performanceSection.noHighlights', 'No public performance highlights available.')}</p>;
    }
    const heroStat = publicStats[0];
    const secondaryStats = publicStats.slice(1);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <div className="lg:col-span-1">
                <HeroStatCard stat={heroStat} />
            </div>
            {secondaryStats.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {secondaryStats.map((stat, idx) => (
                        <StatDisplayCard key={stat.label + idx} stat={stat} index={idx} />
                    ))}
                </div>
            )}
        </div>
    );
  };

  const OwnerAnalyticsView = () => {
    if (!agentData.ownerAnalyticsSummary) {
      return (
        <div className="text-center py-8">
            <BarChart2 size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-slate-500">Analytics data is not yet available for this agent.</p>
        </div>
      );
    }
    const analytics = agentData.ownerAnalyticsSummary;
    const ownerStats: PerformanceStatV10[] = [
      { label: "Unique Users (30d)", value: String(analytics.uniqueUsersLast30Days), icon: 'users', tooltip: "Number of distinct users interacting with the agent." },
      { label: "Platform Costs (30d)", value: analytics.costOfUsageLast30Days, icon: 'dollar' },
      { label: "Avg. Response Time", value: analytics.avgResponseTime, icon: 'clock', tooltip:"Average time taken by the agent to respond." },
      ...analytics.satisfactionScore !== undefined ? [{ label: "Avg. Satisfaction", value: `${analytics.satisfactionScore}/5`, icon: 'smile', trend: analytics.satisfactionScore >= 4 ? 'positive' : analytics.satisfactionScore < 3 ? 'negative' : 'neutral' as any, tooltip: "Average user satisfaction rating." }] : [],
      ...analytics.totalEarnedLast30Days ? [{ label: "Earnings (30d)", value: analytics.totalEarnedLast30Days, icon: 'trendingup', trend: 'positive' as const, tooltip: "Total revenue generated by the agent." }] : [],
      ...analytics.avgSpendPerUser ? [{ label: "Avg. Spend / User", value: analytics.avgSpendPerUser, icon: 'dollar', tooltip: "Average platform cost per unique user." }] : [],
      ...analytics.errorRate !== undefined ? [{ label: "Error Rate", value: `${analytics.errorRate}%`, icon: 'alert', trend: analytics.errorRate > 5 ? 'negative' : analytics.errorRate < 1 ? 'positive' : 'neutral' as any, tooltip: "Percentage of interactions resulting in an error." }] : [],
      ...analytics.conversionRateDetails ? [{ label: `${analytics.conversionRateDetails.goalName}`, value: `${analytics.conversionRateDetails.rate}%`, icon: 'target', trend: analytics.conversionRateDetails.trend, tooltip: `Conversion rate for the goal: ${analytics.conversionRateDetails.goalName}` }] : [],
    ];

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {ownerStats.map((stat, idx) => (
            <StatDisplayCard key={stat.label} index={idx} stat={stat} />
          ))}
        </div>
        
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-4">
            {agentData.fullAnalyticsDashboardUrl && (
            <Button
                text="Full Analytics Dashboard"
                variant="primary"
                icon={<ExternalLink size={16} />}
                iconPosition="right"
                onClick={() => onAction('VIEW_FULL_ANALYTICS', { url: agentData.fullAnalyticsDashboardUrl })}
            />
            )}
            {analytics.detailedPlatformSpendBreakdownUrl && (
                <Button
                    text="Spend Breakdown"
                    variant="outline"
                    icon={<Info size={16} />}
                    iconPosition="right"
                    onClick={() => onAction('VIEW_SPEND_BREAKDOWN', {url: analytics.detailedPlatformSpendBreakdownUrl })}
                />
            )}
        </div>
      </div>
    );
  };

  return (
    <Card
      id={id}
      ref={ref}
      title={isOwnerView ? t('performanceSection.ownerTitle', "Performance & Analytics") : (agentData.performanceHighlightsPublic?.title || t('performanceSection.defaultTitle', "Performance Highlights"))}
      headerBgColor={headerBgColor}
      icon={<Activity size={22} className="text-orange-600" />}
      actionButton={
        isOwnerView && (
          <Button
            text="Analytics Settings"
            variant="outline"
            size="sm"
            icon={<Edit2 size={14} />}
            onClick={() => onAction('MANAGE_ANALYTICS_SETTINGS', null)}
            ariaLabel="Manage analytics settings or report configurations"
          />
        )
      }
    >
      {isOwnerView ? <OwnerAnalyticsView /> : <PublicPerformanceView />}
    </Card>
  );
});

PerformanceSection.displayName = "PerformanceSection";
